import type { ReactNode } from "react";

export interface SegmentedOption<T extends string = string> {
  value: T;
  label: string;
  /** Leading icon (or text glyph like a flag emoji) rendered before the label. */
  icon?: ReactNode;
  /** Leading colored bullet (CSS color value) rendered before the label. */
  dot?: string;
  /** Trailing colored bullet (CSS color value) rendered after the label —
   * useful for completion / validity indicators. */
  trailingDot?: string;
  /** Trailing count rendered after the label in a monospace size. */
  count?: number;
}

export interface SegmentedProps<T extends string = string> {
  value: T;
  onChange: (value: T) => void;
  options: SegmentedOption<T>[];
  ariaLabel?: string;
  className?: string;
  /**
   * "light" — default; light surface background, used in card / form contexts.
   * "dark"  — translucent over a dark hero, white-on-active styling.
   */
  tone?: "light" | "dark";
}

/**
 * Compact segmented control (pill-style). Two-to-six options. Active option
 * gets a filled chip; inactive options sit on the container background. Each
 * option can carry an icon, a colored dot, and a trailing count. Use
 * `tone="dark"` when placing on a dark page hero.
 */
export function Segmented<T extends string = string>({
  value,
  onChange,
  options,
  ariaLabel,
  className = "",
  tone = "light",
}: SegmentedProps<T>) {
  const containerClass =
    tone === "dark"
      ? "inline-flex h-[34px] items-center gap-0.5 rounded-[10px] border border-white/10 bg-white/5 p-[3px]"
      : "inline-flex h-[34px] items-center gap-0.5 rounded-[10px] border border-[color:var(--border-light)] bg-[color:var(--surface-alt)] p-[3px]";

  const tabClass = (active: boolean) => {
    if (tone === "dark") {
      return active
        ? "inline-flex items-center gap-1.5 rounded-[7px] bg-white h-[26px] px-3 text-[12.5px] font-medium text-[#0b1220] transition-colors"
        : "inline-flex items-center gap-1.5 rounded-[7px] bg-transparent h-[26px] px-3 text-[12.5px] font-medium text-white/70 transition-colors hover:text-white";
    }
    return active
      ? "inline-flex items-center gap-1.5 rounded-[7px] bg-[color:var(--surface)] h-[26px] px-3 text-[12.5px] font-medium text-[color:var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)] transition-colors"
      : "inline-flex items-center gap-1.5 rounded-[7px] h-[26px] px-3 text-[12.5px] font-medium text-[color:var(--text-secondary)] transition-colors hover:text-[color:var(--text-primary)]";
  };

  const countClass = (active: boolean) => {
    if (tone === "dark") {
      return active
        ? "text-[10.5px] text-[rgba(11,18,32,0.55)]"
        : "text-[10.5px] text-white/50";
    }
    return "text-[10.5px] text-[color:var(--text-muted)]";
  };

  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={`${containerClass} ${className}`}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(option.value)}
            className={tabClass(active)}
          >
            {option.dot ? (
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: option.dot }}
              />
            ) : null}
            {option.icon ? (
              <span aria-hidden="true" className="inline-flex">
                {option.icon}
              </span>
            ) : null}
            {option.label}
            {option.trailingDot ? (
              <span
                aria-hidden="true"
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: option.trailingDot }}
              />
            ) : null}
            {typeof option.count === "number" ? (
              <span className={`font-mono ${countClass(active)}`}>
                {option.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
